import api from './api';
import type { LoginRequest, LoginResponse, User } from '../types';

export const authService = {
  sendSignupCode: async (email: string): Promise<void> => {
    const response = await api.post<{ success: boolean; error?: string }>(
      '/api/auth/send-signup-code',
      { email: email.trim().toLowerCase() }
    );
    if (!response.success) throw new Error(response.error || 'Failed to send verification code');
  },

  verifySignupCode: async (email: string, code: string): Promise<void> => {
    const response = await api.post<{ success: boolean; error?: string }>(
      '/api/auth/verify-signup-code',
      { email: email.trim().toLowerCase(), code: code.trim() }
    );
    if (!response.success) throw new Error(response.error || 'Verification failed');
  },

  signUp: async (userData: {
    name: string;
    email: string;
    password: string;
    code: string;
    role?: 'PROCUREMENT' | 'VENDOR';
    company?: string;
    designation?: string;
    mobile?: string;
  }): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/api/auth/signup', userData);
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to sign up');
  },

  signIn: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/api/auth/signin', credentials);
    if (response.success && response.data) return response.data;
    throw new Error(response.error || 'Failed to sign in');
  },

  signOut: async (): Promise<void> => {},

  forgotPassword: async (email: string): Promise<void> => {
    const response = await api.post<{ success: boolean; error?: string }>(
      '/api/auth/forgot-password',
      { email: email.trim().toLowerCase() }
    );
    if (!response.success) throw new Error(response.error || 'Failed to send reset code');
  },

  resetPassword: async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> => {
    const response = await api.post<{ success: boolean; error?: string }>(
      '/api/auth/reset-password',
      {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        newPassword,
      }
    );
    if (!response.success) throw new Error(response.error || 'Failed to reset password');
  },
};

export async function fetchCurrentUser(): Promise<User | null> {
  const response = await api.get<User>('/api/users/me');
  if (response.success && response.data) return response.data;
  return null;
}
